"use client";

import { useState, useEffect, useMemo, type ChangeEvent, useRef } from "react";
import { motion, useInView, animate, type Variants } from "framer-motion";
import Image from "next/image";
import { createPortal } from "react-dom";
import type { CurrencyOption } from "@/types/currency";
import type { ProviderCard } from "@/types/provider";
import { useProviderComparison } from "@/hooks/use-provider-comparison";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import CurrencyPickerCompare from "../currency/CurrencyPickerCompare";
import { CTAButtons } from "@/components/ui/action-buttons";
import { useTranslation } from "../language/hooks/useTranslation";
import { AlertCircle, ChevronDown } from "lucide-react";

type CardProps = {
  card: ProviderCard;
  isHighlighted?: boolean;
  currencySymbol: string;
  baseCurrencySymbol: string;
  vellRate: number;
};

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];
const VIEWPORT = { once: false, margin: "10%" };

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
 
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};
 

function formatAmount(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return "0.00";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function resolveCurrencySymbol(symbol?: string | null, code?: string | null): string {
  const trimmedSymbol = symbol?.trim();
  if (trimmedSymbol) return trimmedSymbol;
  const trimmedCode = code?.trim();
  return trimmedCode || "";
}

export default function CompareProviders({
  current_path_match = false,
}: {
  current_path_match?: boolean;
}) {
  const [amount, setAmount] = useState<number>(500);
  const [debouncedAmount, setDebouncedAmount] = useState<number>(500);
  const [toCurrency, setToCurrency] = useState<CurrencyOption>({
    currency_code: "USD",
    currency_name: "US Dollar",
    country_name: "United States",
    code_alpha2: "us",
  });
  const { t, tm, ti } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAmount(amount), 500);
    return () => clearTimeout(timer);
  }, [amount]);

  const {
    data: comparison,
    isLoading,
    isFetching,
    isError,
    error,
  } = useProviderComparison({
    from: "AUD",
    to: toCurrency.currency_code,
    amount: debouncedAmount,
  });

  useEffect(() => {
    if (isError) {
    }
  }, [isError, error]);

  const targetCurrencyCode = comparison?.to_currency || toCurrency.currency_code;
  const baseCurrencyCode = comparison?.from_currency || "AUD";
  const targetCurrencySymbol = resolveCurrencySymbol(
    comparison?.to_currency_symbol,
    targetCurrencyCode,
  );
  const baseCurrencySymbol = resolveCurrencySymbol(
    comparison?.from_currency_symbol,
    baseCurrencyCode,
  );

  const cards = useMemo<ProviderCard[]>(() => {
    if (!comparison) return [];

    const providers = (comparison.wise_comparison || []).slice(0, 3);

    const all: ProviderCard[] = [
      {
        type: "rc",
        name: "Vell",
        logo: "/V_transparent.png",
        received: comparison.converted_amount ?? 0,
        rate: comparison.rate ?? 0,
        fee: comparison.fee ?? 0,
      },
      ...providers.map((p: any) => {
        const q = p.quotes?.[0] ?? {};
        return {
          type: "provider" as const,
          name: String(p.name ?? ""),
          logo: String(p.logos?.circle?.svgUrl || p.logos?.normal?.svgUrl || ""),
          received: Number(q.receivedAmount ?? 0),
          rate: Number(q.rate ?? 0),
          fee: Number(q.fee ?? 0),
          markup: Number(q.markup ?? 0),
          markup_fee: Number(q.markup_fee ?? 0),
          total: Number(q.total_fee ?? 0),
        };
      }),
    ];

    return all.sort((a, b) => {
      if (a.type === "rc") return -1;
      if (b.type === "rc") return 1;
      return (b.received ?? 0) - (a.received ?? 0);
    });
  }, [comparison]);

  const vellRate = comparison?.rate ?? 0;

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/,/g, "");
    if (raw === "") {
      setAmount(0);
      return;
    }
    if (!/^\d+$/.test(raw)) return;
    const next = Number(raw);
    if (!Number.isNaN(next)) {
      setAmount(next);
    }
  };

  return (
    <section className="relative py-20 sm:py-24 bg-brand-bg">

      <div className="px-4 mx-auto lg:max-w-5xl xl:max-w-6xl 3xl:max-w-[1432px] xl:px-0">

        <motion.div
          initial={{ opacity: 0.01, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.6, ease: EASE }}
          className="text-center mx-auto max-w-[800px] 2xl:max-w-[1024px]"
        >
          <h2 className="section-title">
            {t("dashboard.compare.title")}
          </h2> 
          <p className="section-subtitle text-brand-white max-w-[500px] 2xl:max-w-[750px]">
            {t("dashboard.compare.description")}
          </p>
          <div className="max-xs:hidden">
            <CTAButtons
              primary={{
                text: t("dashboard.compare.getStarted"),
                url: "/register",
              }}
              secondary={{
                text: t("dashboard.compare.learnMore"),
                url: current_path_match ? "/frequently-asked-questions" : "/resources/international-money-transfer-comparison",
              }}
            />
          </div>
        </motion.div>
              
        <div className="p-4 sm:p-6 space-y-6 rounded-3xl border-brand-stroke bg-brand-surface mt-6 sm:mt-8">
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
            <div className="max-md:col-span-2">
              <label className="block mb-2 text-sm font-medium text-brand-text 3xl:text-base">
                {t("dashboard.compare.amount")}
              </label>
              <input
                type="text"
                value={amount != null && !isNaN(amount) ? amount.toLocaleString() : ""}
                onChange={handleAmountChange}
                className="w-full px-4 py-3 text-brand-text border input-border-radius bg-brand-bg border-brand-stroke placeholder:text-brand-muted transition-colors input-focus"
                placeholder="1,000"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-brand-text 3xl:text-base">
                {t("dashboard.compare.from")}
              </label>
              <div className="flex items-center gap-2 px-4 py-3 border input-border-radius border-brand-stroke bg-brand-bg">
                <div className="relative w-6 h-6 rounded-full overflow-hidden shrink-0 ring-1 ring-black/[0.06]">
                  <Image src="/flags/au.svg" alt="AUD" fill className="object-cover"/>
                </div>
                <span className="font-medium text-brand-text whitespace-nowrap truncate">
                  AUD <span className="max-xs:hidden">- Australia dollar</span>
                </span>
              </div>
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-brand-text 3xl:text-base">
                {t("dashboard.compare.to")}
              </label>
              <CurrencyPickerCompare value={toCurrency} onChange={setToCurrency} />
            </div>
          </div>

         {isLoading ? (
            <SkeletonGridNew />
          ) : cards.length === 0 ? (
            <EmptyState hasAmount={debouncedAmount > 0} />
          ) : (
            
             <div className={cn("transition-opacity duration-300", isFetching ? "opacity-50 pointer-events-none" : "opacity-100")}>
                {/*++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
                * DESKTOP (lg+): side-by-side label column + provider columns.
                * TABLET (md–lg): hidden — we use the stacked card grid below.
                * MOBILE (< md): hidden — accordion cards below.
                */}
                <div className="hidden lg:grid lg:grid-cols-[200px_repeat(4,minmax(0,1fr))] gap-3 xl:gap-4 px-6 rounded-3xl bg-brand-bg py-8">
                  <div className="grid grid-rows-[auto_repeat(5,auto)] gap-6 rounded-3xl">
                    <div className="h-20" />
                    <div className="text-sm 3xl:text-base font-semibold text-white leading-tight">
                      {t("dashboard.compare.recipient")}
                      <p className="text-xs 3xl:text-sm text-white/40 leading-tight">{t("dashboard.compare.noteRecipient")}</p>
                    </div>
                    <div className="text-sm 3xl:text-base font-semibold text-white leading-tight">{t("dashboard.compare.fee")}</div>
                    <div className="text-sm 3xl:text-base font-semibold text-white leading-tight">{t("dashboard.compare.exchange")}</div>
                    <div className="text-sm 3xl:text-base font-semibold text-white leading-tight">
                      {t("dashboard.compare.rate")}
                      <p className="text-xs text-brand-text/45 3xl:text-sm">(1 AUD → {targetCurrencySymbol})</p>
                    </div>
                    <div className="text-sm 3xl:text-base font-semibold text-white leading-tight">{t("dashboard.compare.total")}</div>
                  </div>
                  {cards.map((card, index) => (
                      <ProviderCardDesktop
                        key={`desktop-${card.type}-${index}`}
                        card={card}
                        isHighlighted={card.type === "rc"}
                        currencySymbol={targetCurrencySymbol}
                        baseCurrencySymbol={baseCurrencySymbol}
                        vellRate={vellRate}
                      />
                  ))}
                </div>
                <div className="hidden md:grid lg:hidden grid-cols-2 gap-4 px-6 py-8 rounded-3xl bg-brand-bg">
                    {cards.map((card, index) => (
                      <ProviderCardTablet
                        key={`tablet-${card.type}-${index}`}
                        card={card}
                        isHighlighted={card.type === "rc"}
                        currencySymbol={targetCurrencySymbol}
                        baseCurrencySymbol={baseCurrencySymbol}
                        vellRate={vellRate}
                      />
                    ))}
                  </div>
                  <div className="flex flex-col gap-3 md:hidden max-xs:px-0 max-xs:py-0 px-6 py-8 rounded-3xl xs:bg-brand-bg">
                    {cards.map((card, index) => (
                      <ProviderCardMobile
                        key={`mobile-${card.type}-${index}`}
                        card={card}
                        isHighlighted={card.type === "rc"}
                        currencySymbol={targetCurrencySymbol}
                        baseCurrencySymbol={baseCurrencySymbol}
                        vellRate={vellRate}
                      />
                    ))}
                  </div>
              </div>
          )}
        </div>

      </div>

      <motion.div
        initial={{ opacity: 0.01, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={VIEWPORT}
        transition={{ duration: 0.6, ease: EASE }}
        className="text-center mx-auto max-w-[800px]"
      >
        <div className="xs:hidden px-4">
          <CTAButtons
            primary={{
              text: t("dashboard.compare.getStarted"),
              url: "/register",
            }}
            secondary={{
              text: t("dashboard.compare.learnMore"),
              url: current_path_match ? "/frequently-asked-questions" : "/resources/international-money-transfer-comparison",
            }}
          />
        </div>
      </motion.div>
    </section>
  );
}

function getCardMetrics(p: ProviderCard, vellRate: number) {
  const totalBase = p.total ?? p.fee ?? null;
  const feeBase = p.fee ?? null;
  const markupFeeBase = p.markup_fee ?? null;
  const markupRate = p.markup ?? null;
  const rateDisplay =
    p.rate != null && !Number.isNaN(p.rate)
      ? p.rate.toLocaleString(undefined, { maximumFractionDigits: 4 })
      : "-";
  const markupDifference = markupRate != null ? formatAmount(markupRate) : "-";
  const markupFeeDisplay = vellRate.toLocaleString(undefined, { maximumFractionDigits: 4 }) ?? "-";
  const tooltipMessage =
    markupRate != null && markupFeeBase != null
      ? `The exchange rate they're using is ${rateDisplay}, which is ${markupDifference}% lower than the mid-market rate of ${markupFeeDisplay}`
      : "The exchange rate they're using is based on the provider quote.";
  return { totalBase, feeBase, markupFeeBase, tooltipMessage };
}

function ProviderCardDesktop({ card: p, isHighlighted, currencySymbol, baseCurrencySymbol, vellRate }: CardProps) {
  const { t } = useTranslation();
  const { totalBase, feeBase, markupFeeBase, tooltipMessage } = getCardMetrics(p, vellRate);
  const vellToolTipFee = t("dashboard.compare.vip");

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, filter: "blur(2px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      className={[
        "relative isolate rounded-3xl border border-brand-stroke text-center flex-col transition-all grid grid-rows-[auto_repeat(5,auto)] gap-5 px-4 py-6",
        isHighlighted ? "bg-brand-text text-brand-black" : "bg-brand-surface/80 text-brand-white",
      ].join(" ")}
    >
      {isHighlighted && (
        <motion.div
          animate={{ opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -inset-1 rounded-3xl bg-brand-white blur-lg -z-10 pointer-events-none"
        />
      )}

      <div className={
        cn("flex flex-col items-center gap-2 text-sm font-semibold min-h-20 3xl:text-base",
          isHighlighted ? "border-brand-grey border-b" : ""
      )}>
        {p.logo ? (
          <Image width={48} height={48} src={p.logo} alt={p.name} className="object-contain" />
        ) : (
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              p.type === "rc" ? "bg-brand-green/70" : "bg-brand-stroke"
            }`}
          >
            <span className="font-semibold text-brand-bg">{p.name.charAt(0)}</span>
          </div>
        )}
        <p className={`${isHighlighted ? "text-brand-card uppercase" : "text-brand-grey"} min-h-[42px] leading-tight`}>{p.name}</p>
      </div>
      <MetricNew className="text-base" value={p.received} currencySymbol={currencySymbol} isHighlighted={isHighlighted} isBold />
      <MetricNew value={feeBase} currencySymbol="AUD" />
      <MetricNew value={p.rate} currencySymbol={baseCurrencySymbol} tooltip={p.type === "rc" ? "" : tooltipMessage} />
      <MetricNew value={markupFeeBase} />
      {/* <MetricNew value={feeBase} currencySymbol="AUD" /> */}
      <MetricNew value={totalBase} currencySymbol={baseCurrencySymbol} tooltip={p.type === "rc" ? vellToolTipFee : ""} isHighlighted={isHighlighted} isBold />
    </motion.div>
  );
}

function ProviderCardTablet({ card: p, isHighlighted, currencySymbol, baseCurrencySymbol, vellRate }: CardProps) {
  const { t } = useTranslation();
  const { totalBase, feeBase, markupFeeBase, tooltipMessage } = getCardMetrics(p, vellRate);
  const vellToolTipFee = t("dashboard.compare.vip");

  return (
    <div
      className={cn(
        "rounded-xl border p-4 flex flex-col gap-3 transition-all",
        isHighlighted
          ? "bg-brand-text text-brand-black border-brand-stroke"
          : "bg-brand-surface/80 text-brand-white border-brand-stroke",
      )}
    >
      <div className="flex items-center gap-3 pb-3 border-b border-brand-card/10">
        {p.logo ? (
          <Image width={36} height={36} src={p.logo} alt={p.name} className="object-contain flex-shrink-0" />
        ) : (
          <div
            className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${
              p.type === "rc" ? "bg-brand-green/70" : "bg-brand-stroke"
            }`}
          >
            <span className="font-semibold text-brand-bg text-sm 3xl:text-base">{p.name.charAt(0)}</span>
          </div>
        )}
        <span
          className={cn(
            "font-semibold text-sm 3xl:text-base line",
            isHighlighted ? "text-brand-card uppercase" : "text-brand-grey",
          )}
        >
          {p.name}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className={cn("text-xs 3xl:text-sm", isHighlighted ? "text-brand-card" : "text-brand-grey")}>
          {t("dashboard.compare.recipient")}
        </span>
        <span className={cn("text-base font-bold 3xl:text-lg", isHighlighted ? "text-brand-card" : "text-brand-white")}>
          {p.received != null && !isNaN(p.received)
            ? p.received.toLocaleString(undefined, { maximumFractionDigits: 2 })
            : "0"}{" "}
          {currencySymbol}
        </span>
      </div>

      <div className="h-px bg-brand-card/10" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs 3xl:text-sm">
        <TabletMetricRow
          label={t("dashboard.compare.fee")}
          value={feeBase}
          currencySymbol="AUD"
          isHighlighted={isHighlighted}
        />
        <TabletMetricRow
          label={t("dashboard.compare.exchange")}
          value={p.rate}
          currencySymbol={baseCurrencySymbol}
          tooltip={p.type === "rc" ? "" : tooltipMessage}
          isHighlighted={isHighlighted}
        />
        <TabletMetricRow
          label={t("dashboard.compare.rate")}
          value={markupFeeBase}
          isHighlighted={isHighlighted}
        />
        <TabletMetricRow
          label={t("dashboard.compare.total")}
          value={totalBase}
          currencySymbol={baseCurrencySymbol}
          tooltip={p.type === "rc" ? vellToolTipFee : ""}
          bold
          isHighlighted={isHighlighted}
        />
      </div>
    </div>
  );
}

function TabletMetricRow({
  label,
  value,
  currencySymbol,
  tooltip,
  bold,
  isHighlighted,
}: {
  label: string;
  value: number | null | undefined;
  currencySymbol?: string;
  tooltip?: string;
  bold?: boolean;
  isHighlighted?: boolean;
}) {
  const display =
    value == null || isNaN(value)
      ? "0"
      : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          "text-xs flex items-center gap-1 3xl:text-sm",
          isHighlighted ? "text-brand-card" : "text-brand-grey",
        )}
      >
        {tooltip ? <InfoTooltip message={tooltip} /> : null}
        {label}
      </span>
      <span
        className={cn(
          "text-xs 3xl:text-sm",
          isHighlighted ? "text-brand-card" : "text-brand-white",
          bold ? "font-bold" : "font-medium",
        )}
      >
        {display}
        {currencySymbol ? ` ${currencySymbol}` : ""}
      </span>
    </div>
  );
}

function ProviderCardMobile({ card: p, isHighlighted, currencySymbol, baseCurrencySymbol, vellRate }: CardProps) {
  const { tooltipMessage, totalBase, feeBase, markupFeeBase } = getCardMetrics(p, vellRate);

  return (
    <div
      className={cn(
        "w-full rounded-xl",
        isHighlighted
          ? "bg-brand-text text-brand-black"
          : "bg-brand-surface/80 border border-brand-stroke shadow-sm text-brand-white",
      )}
    >
      <Accordion type="single" collapsible defaultValue="details">
        <AccordionItem value="details" className="border-none">
          <AccordionTrigger
            className={cn(
              "flex items-center px-3 py-3 hover:no-underline",
              isHighlighted ? "text-brand-black" : "text-brand-white",
            )}
          >
            <div className="flex items-center justify-between w-full gap-4">
              <div className="flex items-center gap-2 font-semibold text-sm 3xl:text-base">
                {p.logo ? (
                  <Image width={24} height={24} src={p.logo} alt={p.name} className="object-contain" />
                ) : (
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      p.type === "rc" ? "bg-brand-green/70" : "bg-brand-stroke"
                    }`}
                  >
                    <span className="font-semibold text-brand-bg text-xs">{p.name.charAt(0)}</span>
                  </div>
                )}
                {p.name}
              </div>
              <MetricNew
                value={p.received}
                currencySymbol={currencySymbol}
                isBold
                isHighlighted={isHighlighted}
                className={cn("text-end", isHighlighted ? "text-brand-black" : "text-brand-white")}
              />
            </div>
          </AccordionTrigger>

          <AccordionContent className="px-3 pb-4">
            <div
              className={cn(
                "relative p-3 text-sm rounded-lg 3xl:text-base",
                isHighlighted ? "bg-brand-white text-brand-card" : "bg-brand-surface text-brand-grey",
              )}
            >
              <div className="flex items-center justify-between py-1">
                <span>Transfer fee</span>
                <MetricNew value={feeBase} currencySymbol="AUD" isHighlighted={isHighlighted} />
              </div>
              <div className="flex items-center justify-between py-1">
                <span>Exchange rate</span>
                <MetricNew value={p.rate} isHighlighted={isHighlighted} />
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="block items-center gap-1">
                  Exchange rate markup <br />
                  <span className="text-xs font-light 3xl:text-sm"> (1 AUD -&gt; {currencySymbol})</span> &nbsp;
                  {p.type === "rc" ? "" : <InfoTooltip message={tooltipMessage} />}
                </span>
                <MetricNew value={markupFeeBase} currencySymbol={baseCurrencySymbol} isHighlighted={isHighlighted} />
              </div>
              <div className="flex items-center justify-between pt-2 mt-2 font-medium border-t border-brand-stroke">
                <span className="font-semibold">Cost of transfer</span>
                <MetricNew value={totalBase} currencySymbol={baseCurrencySymbol} isHighlighted={isHighlighted} />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function MetricNew({
  value,
  isBold,
  isHighlighted = true,
  currencySymbol,
  tooltip,
  className,
}: {
  value: number | null | undefined;
  isHighlighted?: boolean;
  isBold?: boolean;
  subtle?: boolean;
  currencySymbol?: string;
  tooltip?: string;
  className?: string;
}) {
  const display =
    value == null || isNaN(value)
      ? "0"
      : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return (
    <div
      className={cn(
        "flex gap-2 h-8 justify-center items-center text-[14px] 3xl:text-base",
        isBold ? "font-bold text-brand-card" : "font-normal",
        !isHighlighted ? "text-brand-grey font-semibold flex gap-2 h-8" : "",
        className,
      )}
    >
      {display} {currencySymbol ? ` ${currencySymbol}` : ""}
      {tooltip ? <InfoTooltip message={tooltip} /> : null}
    </div>
  );
}

function InfoTooltip({ message }: { message: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
 
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top + window.scrollY - 8,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  };
 
  const openTooltip = () => {
    updatePosition();
    setIsOpen(true);
  };
 
  return (
    <span
      ref={triggerRef}
      className="group relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-grey text-xs font-semibold text-brand-text cursor-pointer"
      onClick={() => (isOpen ? setIsOpen(false) : openTooltip())}
      onMouseEnter={openTooltip}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div className="relative flex items-center justify-center w-full h-full rounded-full overflow-hidden">
        <span className={`text-brand-black transition-opacity duration-200 ${isOpen ? "opacity-0" : "opacity-100"}`}>
          !
        </span>
        <Image
          src="/vell.png"
          alt="logo"
          width={18}
          height={18}
          className={`absolute inset-0 m-auto transition-opacity duration-200 ${
            isOpen ? "opacity-100" : "opacity-0"
          }`}
        />
      </div>
 
      <span className="sr-only">{message}</span>
 
      {typeof document !== "undefined" &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              transform: "translate(-50%, -100%)",
              zIndex: 9999,
            }}
            className={`w-44 rounded-md bg-brand-surface border border-brand-stroke px-3 py-2 text-xs font-medium text-brand-text shadow-lg transition duration-150 ease-out ${
              isOpen ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {message}
            <span className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 h-3 w-3 rotate-45 bg-brand-surface border-r border-b border-brand-stroke" />
          </div>,
          document.body
        )}
    </span>
  );
}

function SkeletonGridNew() {
  return (
    <>
      <div className="hidden lg:grid lg:grid-cols-[180px_repeat(4,minmax(0,1fr))] gap-3">
        {Array.from({ length: 5 }).map((_, idx) => (
          <div key={idx} className="rounded-3xl bg-brand-text/90 grid grid-rows-[auto_repeat(5,auto)] gap-4 px-4 py-6 animate-pulse">
            <div className="w-full h-24 rounded bg-brand-stroke" />
            {Array.from({ length: 5 }).map((__, i) => (
              <div key={i} className="h-8 rounded bg-brand-stroke" />
            ))}
          </div>
        ))}
      </div>

      {/* Tablet skeleton */}
      <div className="hidden md:grid lg:hidden grid-cols-2 gap-4 ">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="rounded-3xl bg-brand-text/90 p-4 flex flex-col gap-3 animate-pulse">
            <div className="h-10 rounded bg-brand-stroke" />
            <div className="h-8 rounded bg-brand-stroke" />
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((__, i) => (
                <div key={i} className="h-8 rounded bg-brand-stroke" />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3 md:hidden">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="rounded-3xl bg-brand-card p-4 animate-pulse">
            <div className="h-10 rounded bg-brand-surface" />
          </div>
        ))}
      </div>
    </>
  );
}

function EmptyState({ hasAmount }: { hasAmount: boolean }) {
  return (
    <div className="p-10 text-center border border-dashed col-span-full rounded-2xl border-brand-stroke bg-brand-card/90 backdrop-blur">
      <p className="font-medium text-brand-text">
        {hasAmount ? "No comparison data available." : "Enter an amount to compare providers."}
      </p>
      <p className="mt-2 text-sm text-brand-muted">
        {hasAmount
          ? "Try a different amount or currency."
          : "We will show rates, fees, and the estimated payout for each provider."}
      </p>
    </div>
  );
}
